import { clsx } from "clsx";
import { isEqual } from "lodash-es";
import { debounce } from "lodash-es";
import { createRef, type CSSProperties, PureComponent, type ReactNode } from "react";

import Filter from "./Filter.js";
import Option, { type IOptionObject } from "./Option.js";
import Placeholder from "./Placeholder.js";

export interface IMCSRenderSelectAllProps {
  multiple?: boolean;
  filtered: boolean;
  allSelected: boolean;
  toggleSelectAll: () => void;
}

export interface IMCSRenderListProps {
  items: IOptionObject[];
  selected: IOptionObject[];
  getIsSelected: (value: IOptionObject) => boolean;
  selectValue: (value: IOptionObject) => void;
}

export interface IMultiCheckboxSelectProps {
  id: string;
  className: string;
  value: IOptionObject[];
  options: IOptionObject[];
  onChange?: (value: IOptionObject[], clicked: IOptionObject) => void;

  numberDisplayed?: number;
  multiple?: boolean;
  open?: boolean;
  includeSelectAll?: boolean;
  includeFilter?: boolean;
  defaultFocusFilter?: boolean;
  keepOpen?: boolean;

  filterDebounce?: number;
  dropdownHeight?: number;

  onFiltered?: (value: IOptionObject[]) => void;
  onOpen?: () => void;
  onClose?: () => void;

  valueKey: string;
  labelKey: string;

  placeholder?: string;
  manySelectedPlaceholder?: string;
  allSelectedPlaceholder?: string;
  selectAllText?: string;
  filterPlaceholder?: string;

  render?: (a: any) => ReactNode;
  renderSelectAll?: (obj: IMCSRenderSelectAllProps) => ReactNode;
  renderList?: (obj: IMCSRenderListProps) => void;
}

export interface IMultiCheckboxSelectState {
  open?: boolean;
  selectedValue: IOptionObject[];
  filtered: boolean;
  filteredOptions: IOptionObject[];
  allSelected: boolean;
  allOptions: IOptionObject[];
}

export default class CheckboxMultiSelect extends PureComponent<IMultiCheckboxSelectProps, IMultiCheckboxSelectState> {
  public static defaultProps = {
    numberDisplayed: 1,
    options: [],
    value: [],
    filterDebounce: 150,
    dropdownHeight: 300,
    tabIndex: 0,
    keepOpen: true,
    selectAllText: "Select all",
    includeFilter: true,
    defaultFocusFilter: true,
    includeSelectAll: true,
    className: "picky",
  };

  static areEqualOptions = (values1: IOptionObject[], values2: IOptionObject[], valueKey: string): boolean => {
    return isEqual(values1.map((x) => x[valueKey]).sort(), values2.map((x) => x[valueKey]).sort());
  };

  static getDerivedStateFromProps(nextProps: IMultiCheckboxSelectProps, state: IMultiCheckboxSelectState): any {
    if (nextProps.onChange == null) {
      return null;
    }
    if (
      CheckboxMultiSelect.areEqualOptions(nextProps.options, state.allOptions, nextProps.valueKey) &&
      CheckboxMultiSelect.areEqualOptions(nextProps.value, state.selectedValue, nextProps.valueKey)
    ) {
      return null;
    }
    return {
      ...state,
      selectedValue: nextProps.value,
      allOptions: nextProps.options,
      allSelected: CheckboxMultiSelect.areEqualOptions(nextProps.value, nextProps.options, nextProps.valueKey),
    };
  }

  private filterRef = createRef<Filter>();
  private outerRef = createRef<HTMLDivElement>();

  constructor(props: IMultiCheckboxSelectProps) {
    super(props);
    this.state = {
      selectedValue: props.value,
      open: props.open,
      filtered: false,
      filteredOptions: [],
      allSelected: false,
      allOptions: props.options,
    };
  }

  componentDidMount() {
    this.focusFilterInput(!!this.state.open);

    // making sure that the allSelected flag is set correctly when all options are pre-selected when the component mounts
    this.setState({
      allSelected: CheckboxMultiSelect.areEqualOptions(this.props.value, this.props.options, this.props.valueKey),
    });
  }

  componentWillUnmount() {
    document.removeEventListener("click", this.handleOutsideClick, false);
  }

  selectValue = (value: IOptionObject) => {
    const { valueKey } = this.props;
    const selectedIdx = this.state.selectedValue.findIndex((x) => x[valueKey] === value[valueKey]);

    let newSelectedValues: IOptionObject[];
    if (selectedIdx === -1) {
      newSelectedValues = [...this.state.selectedValue, value];
    } else {
      newSelectedValues = [
        ...this.state.selectedValue.slice(0, selectedIdx),
        ...this.state.selectedValue.slice(selectedIdx + 1),
      ];
    }

    this.setState(
      {
        ...this.state,
        allSelected: CheckboxMultiSelect.areEqualOptions(newSelectedValues, this.state.allOptions, valueKey),
        selectedValue: newSelectedValues,
      },
      () => (this.props.onChange != null ? this.props.onChange(newSelectedValues, value) : undefined),
    );
  };

  toggleSelectAll = () => {
    const newSelectedValues = this.state.allSelected ? [] : [...this.state.allOptions];

    this.setState(
      (state) => {
        return {
          ...state,
          selectedValue: newSelectedValues,
          allSelected: !this.state.allSelected,
        };
      },
      () => {
        if (this.props.onChange != null) {
          this.props.onChange(newSelectedValues, null);
        }
      },
    );
  };

  isItemSelected = (item: IOptionObject): boolean => {
    const valueKey = this.props.valueKey;
    return (
      [...this.props.value, ...this.state.selectedValue].findIndex(
        (x: IOptionObject) => x[valueKey] === item[valueKey],
      ) > -1
    );
  };

  renderPlainList = (items: IOptionObject[]) => {
    const { labelKey, valueKey, render, renderList } = this.props;

    if (renderList) {
      return renderList({
        items,
        selected: this.props.value,
        getIsSelected: this.isItemSelected,
        selectValue: this.selectValue,
      });
    }
    return items.map((item: IOptionObject, index: number) => {
      // Create a key based on the allOptions value
      const key = item[valueKey];

      const isSelected = this.isItemSelected(item);
      // If render prop supplied for items call that.
      if (typeof render === "function") {
        return render({
          index,
          item,
          isSelected,
          selectValue: this.selectValue,
          labelKey: labelKey,
          valueKey: valueKey,
        });
      } else {
        // Render a simple option
        return (
          <Option
            key={key}
            item={item}
            isSelected={isSelected}
            selectValue={this.selectValue}
            labelKey={labelKey}
            valueKey={valueKey}
          />
        );
      }
    });
  };

  renderOptions = () => {
    return this.renderPlainList(this.state.filtered ? this.state.filteredOptions : this.state.allOptions);
  };

  onFilterChange = (term: any) => {
    if (!term.trim()) {
      return this.setState({
        filtered: false,
        filteredOptions: [],
      });
    }

    const filteredOptions = this.state.allOptions.filter((option) =>
      option[this.props.labelKey].toLowerCase().includes(term.trim().toLowerCase()),
    );

    this.setState(
      {
        filtered: true,
        filteredOptions,
      },
      () => {
        if (this.props.onFiltered) {
          this.props.onFiltered(filteredOptions);
        }
      },
    );
  };

  handleOutsideClick = (e: Event) => {
    const keepOpen = this.props.keepOpen;
    if (this.outerRef && this.outerRef.current && this.outerRef.current.contains(e.target as Node) && keepOpen) {
      return;
    }
    this.toggleDropDown();
  };

  focusFilterInput = (isOpen: boolean) => {
    if (isOpen && this.props.defaultFocusFilter) {
      if (
        this.filterRef &&
        this.filterRef.current &&
        this.filterRef.current.getRef() &&
        this.filterRef.current.getRef().current
      ) {
        const inputElement = this.filterRef.current.getRef().current;
        if (inputElement != null) {
          inputElement.focus();
        }
      }
    }
  };

  toggleDropDown = () => {
    if (!this.state.open) {
      document.addEventListener("click", this.handleOutsideClick, false);
    } else {
      document.removeEventListener("click", this.handleOutsideClick, false);
    }

    let filtered = this.state.filtered;
    let filteredOptions = this.state.filteredOptions;
    if (this.state.open) {
      filtered = false;
      filteredOptions = [];
      if (this.filterRef.current) {
        this.filterRef.current.clear();
      }
    }

    this.setState(
      (state) => {
        return {
          ...state,
          filtered,
          filteredOptions,
          open: !state.open,
        };
      },
      () => {
        const isOpen = !!this.state.open;
        this.focusFilterInput(isOpen);
        if (isOpen && this.props.onOpen) {
          this.props.onOpen();
        } else if (!isOpen && this.props.onClose) {
          this.props.onClose();
        }
      },
    );
  };

  render() {
    const {
      className,
      placeholder,
      multiple,
      numberDisplayed,
      includeFilter,
      filterDebounce,
      valueKey,
      labelKey,
      dropdownHeight,
      renderSelectAll,
      filterPlaceholder,
      id,
    } = this.props;

    const open = !!this.state.open;

    const dropdownStyle: CSSProperties = { maxHeight: dropdownHeight, overflowY: "scroll" };
    const inputClasses = clsx("picky__input", { open: open });

    return (
      <div className={className} id={id} ref={this.outerRef}>
        <button id={`${this.props.id}__button`} type="button" className={inputClasses} onClick={this.toggleDropDown}>
          <Placeholder
            allSelected={this.state.allSelected}
            placeholder={placeholder}
            manySelectedPlaceholder={this.props.manySelectedPlaceholder}
            allSelectedPlaceholder={this.props.allSelectedPlaceholder}
            value={this.state.selectedValue}
            numberDisplayed={numberDisplayed}
            valueKey={valueKey}
            labelKey={labelKey}
          />
        </button>
        <div className="picky__dropdown" style={open ? dropdownStyle : { visibility: "hidden" }}>
          {includeFilter && (
            <Filter
              ref={this.filterRef}
              placeholder={filterPlaceholder}
              onFilterChange={debounce(this.onFilterChange, filterDebounce)}
            />
          )}
          {renderSelectAll &&
            !!this.state.allOptions.length &&
            renderSelectAll({
              filtered: this.state.filtered,
              allSelected: this.state.allSelected,
              toggleSelectAll: this.toggleSelectAll,
              multiple,
            })}
          {open && <div> {this.renderOptions()}</div>}
        </div>
      </div>
    );
  }
}
